import { commonStrings, summaryManagementStrings } from 'constants/strings';
import type { TSummaryDeliveryStatus, TSummaryMode } from 'types/summary';

/**
 * 一覧(PC table/スマホcard)と詳細paneで共有する表示導出。
 * 同じ判定を各viewに書くと、片側だけ直して「PCとスマホで見え方が違う」
 * 「一覧にある注記が詳細に無い」という不一致が再発するため、ここへ集約する。
 */

export const summaryStatusTone: Record<
  TSummaryDeliveryStatus,
  'neutral' | 'success' | 'warning' | 'danger'
> = {
  RUNNING: 'neutral',
  COMPLETED: 'success',
  PARTIALLY_FAILED: 'warning',
  UNKNOWN: 'warning',
  FAILED: 'danger',
};

/**
 * 一覧の行以外に出すメッセージを決める。行を表示すべきときはnull。
 * 取得失敗でも手元にrowsがあれば隠さない。再取得失敗で既存行が消えると
 * 「該当0件」や障害と誤読され、PC/スマホで見え方も食い違う。
 */
export function resolveSummaryListMessage(input: {
  mode: TSummaryMode;
  loading: boolean;
  hasError: boolean;
  rowCount: number;
}): string | null {
  if (input.loading) return commonStrings.loading;
  if (input.rowCount > 0) return null;
  return input.hasError
    ? summaryManagementStrings.errors.loadFailed
    : summaryManagementStrings.emptyResults[input.mode];
}

/**
 * 送信不可バッジ。0人なら出さない。
 */
export function resolveUnavailableRecipientBadge(summary: {
  unavailableRecipientCount: number;
}): string | null {
  if (summary.unavailableRecipientCount <= 0) return null;
  return summaryManagementStrings.recipientBadges.unavailable.replace(
    '$count',
    summary.unavailableRecipientCount.toLocaleString('ja-JP'),
  );
}

/**
 * 人数・回数の表示。計測対象外(null)は0ではなく「—」にする。
 * 0と表示すると「誰も押していない」と読めてしまい、対応前の配信を誤読させる。
 * 緩い比較なのは既存formatNumberと同じ理由: managementとbackendは別deployなので、
 * 旧APIが新フィールドを返さない窓ではundefinedが来る。厳密比較だと一覧が丸ごと落ちる。
 */
export function formatSummaryCount(value: number | null | undefined): string {
  return value == null
    ? summaryManagementStrings.unavailableMetric
    : value.toLocaleString('ja-JP');
}

/**
 * クリック率の表示。backendが小数第1位まで丸めた値をそのまま出す。
 * 画面側で丸め直すと、同じ数字がAPIと画面でずれる。
 */
export function formatSummaryClickRate(rate: number | null | undefined): string {
  return rate == null
    ? summaryManagementStrings.unavailableMetric
    : `${rate.toFixed(1)}%`;
}

/**
 * 結果不明バッジ。人数を集計できないUNKNOWN(request単位の結果不明のみ)は
 * 人数なしの注記へ落とす(A42)。一覧と詳細で同じ文言を出すこと。
 */
export function resolveUnknownRecipientBadge(summary: {
  status: TSummaryDeliveryStatus;
  unknownRecipientCount: number;
}): string | null {
  if (summary.status !== 'UNKNOWN') return null;
  return summary.unknownRecipientCount > 0
    ? summaryManagementStrings.recipientBadges.unknown.replace(
        '$count',
        summary.unknownRecipientCount.toLocaleString('ja-JP'),
      )
    : summaryManagementStrings.recipientBadges.unknownPartial;
}
